import type { AssetItem, CaseItem, CaseKind, CaseStatus, PmProjectItem } from "./types";

const tokenKey = "vhuiwu-content-admin-token";

export function getToken() { return sessionStorage.getItem(tokenKey); }
export function setToken(token: string | null) {
  if (token) sessionStorage.setItem(tokenKey, token);
  else sessionStorage.removeItem(tokenKey);
}

async function request<T>(url: string, options: RequestInit = {}, authenticated = false): Promise<T> {
  const headers = new Headers(options.headers);
  if (!(options.body instanceof FormData)) headers.set("Content-Type", "application/json");
  if (authenticated) {
    const token = getToken();
    if (!token) throw new Error("ADMIN_AUTH_REQUIRED");
    headers.set("Authorization", `Bearer ${token}`);
  }
  const response = await fetch(url, { ...options, headers });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error || `HTTP_${response.status}`);
  }
  if (response.status === 204) return undefined as T;
  return response.json();
}

export async function login(username: string, password: string) {
  return request<{ token: string }>("/api/admin/session", { method: "POST", body: JSON.stringify({ username, password }) });
}

export async function listPublicCases(kind?: CaseKind) {
  const query = kind ? `?kind=${kind}&pageSize=100` : "?pageSize=100";
  return request<{ items: CaseItem[]; total: number }>(`/api/public/cases${query}`);
}

export async function getPublicCase(slug: string) {
  return request<CaseItem>(`/api/public/cases/${encodeURIComponent(slug)}`);
}

export async function listAdminCases(filters: { kind?: CaseKind; status?: CaseStatus; keyword?: string } = {}) {
  const query = new URLSearchParams({ pageSize: "100" });
  if (filters.kind) query.set("kind", filters.kind);
  if (filters.status) query.set("status", filters.status);
  if (filters.keyword) query.set("keyword", filters.keyword);
  return request<{ items: CaseItem[]; total: number }>(`/api/admin/cases?${query}`, {}, true);
}

export async function getAdminCase(id: string) {
  return request<CaseItem>(`/api/admin/cases/${id}`, {}, true);
}

export async function saveCase(item: Partial<CaseItem> & Pick<CaseItem, "title" | "slug" | "kind">) {
  const url = item.id ? `/api/admin/cases/${item.id}` : "/api/admin/cases";
  return request<{ id?: string; success?: boolean }>(url, { method: item.id ? "PUT" : "POST", body: JSON.stringify(item) }, true);
}

export async function publishCase(id: string) {
  return request<{ success: boolean }>(`/api/admin/cases/${id}/publish`, { method: "POST" }, true);
}

export async function getPmIntegrationStatus() {
  return request<{ configured: boolean; source: string }>("/api/admin/pm/status", {}, true);
}

export async function listPmProjects(keyword = "") {
  const query = new URLSearchParams();
  if (keyword) query.set("keyword", keyword);
  const suffix = query.size ? `?${query}` : "";
  return request<{ items: PmProjectItem[]; total: number; source: string }>(`/api/admin/pm/projects${suffix}`, {}, true);
}

export async function importPmProject(sourceRecordId: string) {
  return request<{ caseId: string; caseStatus: CaseStatus; created: boolean; syncedAssets: number; message: string }>(
    `/api/admin/pm/projects/${encodeURIComponent(sourceRecordId)}/import`,
    { method: "POST" },
    true,
  );
}

export async function listAssets(keyword = "") {
  const query = new URLSearchParams({ pageSize: "100" });
  if (keyword) query.set("keyword", keyword);
  return request<{ items: AssetItem[]; total: number }>(`/api/assets?${query}`, {}, true);
}

export async function uploadAsset(file: File) {
  const body = new FormData();
  body.append("file", file);
  return request<{ id: string; duplicate: boolean }>("/api/assets/upload", { method: "POST", body }, true);
}
